import { parseAssessmentItem } from '../qti/parser/question/QTIAssessmentItem';

import assessmentReader from '../store/readHelpers/AssessmentReader';
import questionReader from '../store/readHelpers/QuestionReader';
import responseReader from '../store/readHelpers/ResponseReader';
import sessionReader from '../store/readHelpers/SessionReader';

import tocActions from './TocActions';
import responseActions from './ResponseActions';
import a11yIcewormService from '../services/a11yIcewormService';

import i18nService from '../services/i18nService';
import responseService from '../services/ResponseService';
import navigationService from '../services/NavigationService';
import storeManager from '../store/StoreManager';
import sessionActions from './SessionActions';
import interactionVisibilityService from '../services/questionView/InteractionVisibilityService';
import utilService from '../services/UtilService';
import objectTypes from '../constants/ObjectTypes';
import themeServices from '../themes/ThemeServices';
import settingsReader from '../store/readHelpers/SettingsReader';

const assessmentActions = {
    types: {
        ASSESSMENT_LOAD_REQUEST: 'ASSESSMENT_LOAD_REQUEST',
        ASSESSMENT_LOAD_FAILED: 'ASSESSMENT_LOAD_FAILED',
        ASSESSMENT_STIMULUS_PARSED: 'ASSESSMENT_STIMULUS_PARSED',
        ASSESSMENT_QUESTION_PARSED: 'ASSESSMENT_QUESTION_PARSED',
        ASSESSMENT_SET_STATE: 'ASSESSMENT_SET_STATE',
        ASSESSMENT_SKETCH_CODES_LOADED: 'ASSESSMENT_SKETCH_CODES_LOADED',
        ASSESSMENT_SKETCH_CODE_LOADED: 'ASSESSMENT_SKETCH_CODE_LOADED',
        ASSESSMENT_FEEDBACK_LOADED: 'ASSESSMENT_FEEDBACK_LOADED',
        ASSESSMENT_FEEDBACK_RESET: 'ASSESSMENT_FEEDBACK_RESET',
        ASSESSMENT_QUESTIONS_RESET_PARSED: 'ASSESSMENT_QUESTION_RESET_PARSED',
        ASSESSMENT_UPDATE_SECTION: 'ASSESSMENT_UPDATE_SECTION',
    },

    /**
     * Adds question numbering to all the questions, e.g: 1.1, 1.2, 1.3...
     *
     * @param assessment The full assessment object from GetQuestionSet.
     */
    addQuestionNumbering(assessment) {
        assessment.sections.forEach((section) => {
            section.questions.forEach((question) => {
                if (settingsReader.useSectionAlphaNumeric()) {
                    if (section.questions.length === 1) {
                        question.number = question.sectionNumber;
                    } else if (question.subQuestionNumber) {
                        question.number = `${question.sectionNumber}(${question.questionNumberingInSection}) (${question.subQuestionNumber})`;
                    } else {
                        question.number = `${question.sectionNumber}(${question.questionNumberingInSection})`;
                    }
                } else if (settingsReader.useSectionNumeric()) {
                    question.number = `${question.sectionNumber}.${question.questionNumberingInSection}`;
                } else {
                    question.number = `${question.questionNumberInQuestionSet}`;
                }
            });
        });
    },

    mergeSections(assessment) {
        themeServices.getThemeInstance().mergeSections(assessment);
    },

    /**
     * Adds item information for collapsing item numbering for the footer and navigation
     *
     * @param assessment The full assessment object from GetQuestionSet.
     */
    appendItemNumberingInfo(assessment) {
        const scorableItems = assessment.questionNumbering.scorableItems;
        let collapsedItemIndex;
        scorableItems.forEach((item, index) => {
            if (item.choiceIdentifier) {
                return;
            }

            const prevItem = scorableItems[index - 1];
            const nextItem = scorableItems[index + 1];

            if (
                nextItem?.responseIdentifier === item.responseIdentifier &&
                nextItem?.contentRevisionId === item.contentRevisionId &&
                (!prevItem || typeof prevItem.collapsedItemIndex === 'undefined' || prevItem.lastCollapsedItem)
            ) {
                // If choiceIdentifier is undefined and next item has the same responseIdentifier
                // and contentRevisionId, numbering should collapse (i.e. exactMatch MPR)
                item.firstCollapsedItem = true;
                collapsedItemIndex = 0;
                item.collapsedItemIndex = collapsedItemIndex;
            } else if (
                prevItem?.responseIdentifier === item.responseIdentifier &&
                prevItem?.contentRevisionId === item.contentRevisionId
            ) {
                // If choiceIdentifier is undefined and previous item has the same responseIdentifier
                // and contentRevisionId, item should be hidden (i.e. exactMatch MPR)
                item.hidden = true;
                collapsedItemIndex++;
                item.collapsedItemIndex = collapsedItemIndex;

                // If next item is undefined, or responseIdentifier or
                // contentRevisionId don't match, set lastCollapsedItem: true
                if (
                    !nextItem ||
                    nextItem.responseIdentifier !== item.responseIdentifier ||
                    nextItem.contentRevisionId !== item.contentRevisionId
                ) {
                    item.lastCollapsedItem = true;
                }
            }
        });
    },

    setWindowTitle(title) {
        // Set silent title in IEP with v4+ and focus handoff enabled to prevent screen reader announcements during focus transitions
        if (a11yIcewormService.isIcewormHandoffEnabled()) {
            window.document.title = '\u200b';
            return;
        }

        if (title !== window.document.title) {
            window.document.title = title;
        }
    },

    shouldParseQuestion(wantedQuestion, wantedLanguage) {
        return wantedQuestion && (!wantedQuestion.isParsed || wantedQuestion.language !== wantedLanguage);
    },

    questionParsed(id, questionModel) {
        return { type: this.types.ASSESSMENT_QUESTION_PARSED, id, questionModel };
    },

    stimulusParsed(id, questionModel) {
        return { type: this.types.ASSESSMENT_STIMULUS_PARSED, id, questionModel };
    },

    optimiseDataStructure(assessment) {
        assessment.sections.forEach((section) => {
            const { questions } = section;
            const nestedSections = Array.isArray(section.sections) ? section.sections : [];
            if (questions.length && questions[0].category === objectTypes.CATEGORY.STIMULUS) {
                const stimulus = questions[0];

                const matchedNestedSection = nestedSections.find(section =>
                    section.questions?.some(q => q.contentRevisionId === stimulus.contentRevisionId)
                );
                if (matchedNestedSection) {
                    section.initialStimulus = JSON.parse(JSON.stringify({
                        ...stimulus,
                        allowStimulusResizing: matchedNestedSection.allowStimulusResizing,
                        layout: matchedNestedSection.layout,
                        stimulusSize: matchedNestedSection.stimulusSize,
                    }));
                }
                section.stimulusContent = questions[0];
                if (questions[0].objectType !== objectTypes.GAP_MATCH) {
                    section.questions.splice(0, 1); // Remove the stimulus question from the questions array.
                }
            }
        });

        return assessment;
    },

    filterBranchRuleTargetSection(assessment) {
        const branchTargetSectionId = utilService.getUrlParam('branchTargetSectionId');

        if (sessionReader.isContextPreview() && assessment.sections.length && assessment.hasBranchRules && branchTargetSectionId) {
            const firstSectionId = assessment.sections[0].id.toString();
            assessment.sections = assessment.sections.filter((section) => {
                const sId = section.id.toString();
                return (sId === branchTargetSectionId || sId === firstSectionId);
            });
        }
    },

    addNestedSectionIds(assessment) {
        assessment.sections.forEach((section) => {
            section.questions.forEach((question) => {
                const hasNestedSectionId = section.sections?.some(nestedSection => nestedSection.id === question.parentId);
                if (hasNestedSectionId) {
                    question.nestedSectionId = question.parentId;
                }
            });
        });
    },

    // TODO This does not belong in an *Actions class (IA1-6135)
    assessmentLoadSuccess(assessment) {
        this.filterBranchRuleTargetSection(assessment);
        this.optimiseDataStructure(assessment);
        this.addQuestionNumbering(assessment);
        this.addNestedSectionIds(assessment);

        if (assessment.questionNumbering && assessment.questionNumbering.scorableItems) {
            this.appendItemNumberingInfo(assessment);
        }
        this.mergeSections(assessment);
        this.setWindowTitle(assessment.title);

        const questionsIndex = assessmentReader.getQuestionsIndex(assessment);

        if (navigationService.shouldNavigateToEmptyQuestionSet(questionsIndex)) {
            storeManager.dispatch(sessionActions.setCriticalErrorOccurred());
            if (navigationService.shouldNavigateToEmptyContentBlock(assessment)) {
                navigationService.navigateToEmptyContentBlockPage();
            } else {
                navigationService.navigateToEmptyQuestionSetPage();
            }
        }

        return (dispatch) => {
            // Warning: These operations are not batch, thus we could have assessment and toc out-of-sync between renders.
            dispatch(this.setAssessmentState(assessment));
            dispatch(tocActions.setQuestionsIndex(questionsIndex));
        };
    },

    setAssessmentState(assessment) {
        return { type: this.types.ASSESSMENT_SET_STATE, assessment };
    },

    assessmentLoadFailed(ex) {
        return { type: this.types.ASSESSMENT_LOAD_FAILED, ex };
    },

    /**
     * Action creator responsible for parsing the question. It checks if the question needs to be parsed.
     * A question will need to be parsed if:
     * - the question has not be parsed before
     * - the question has been parsed on another language
     * @param contentRevisionId
     */
    parseQuestion(contentRevisionId) {
        return (dispatch, getState) => {
            const state = getState();
            const wantedQuestion = assessmentReader.getQuestion(contentRevisionId, state);
            const language = questionReader.getContentLanguageToUse(
                i18nService.getContentLanguageCode(state.session.language),
                wantedQuestion.supportedLanguages,
                wantedQuestion.defaultLanguage,
                assessmentReader.getSupportedLanguages(),
            );
            const assessmentRunId = sessionReader.getAssessmentRunId();

            if (this.shouldParseQuestion(wantedQuestion, language)) {
                // in this case the state should have the parsed question
                const parseInteractionsOnly = sessionReader.isPrintTypeAnswersOnly();
                const parsed = parseAssessmentItem(wantedQuestion, language, assessmentRunId, parseInteractionsOnly);

                dispatch(this.questionParsed(contentRevisionId, parsed));
            }
        };
    },

    prepareQuestion(originalQuestion, isStimulus = false) {
        const state = storeManager.getState();
        const store = storeManager.getStore();
        const question = JSON.parse(JSON.stringify(originalQuestion));
        const language = questionReader.getContentLanguageToUse(
            i18nService.getContentLanguageCode(state.session.language),
            question.supportedLanguages,
            question.defaultLanguage,
            assessmentReader.getSupportedLanguages(),
        );
        const assessmentRunId = sessionReader.getAssessmentRunId();
        let itemResult = responseReader.getItemResultForQuestion(question.contentRevisionId);
        let parsedQuestion;

        if (this.shouldParseQuestion(question, language)) {
            const interactionsOnly = sessionReader.isPrintTypeAnswersOnly();
            parsedQuestion = parseAssessmentItem(question, language, assessmentRunId, interactionsOnly);

            if (isStimulus) {
                store.dispatch(
                    this.stimulusParsed(question.contentRevisionId, parsedQuestion),
                );
            } else {
                store.dispatch(
                    this.questionParsed(question.contentRevisionId, parsedQuestion),
                );
            }
        } else {
            parsedQuestion = question;
        }

        interactionVisibilityService.setInteractionVisibilityIfAvailable(
            parsedQuestion.contentRevisionId,
            parsedQuestion.mediaModels
        );

        if (!itemResult) {
            itemResult = responseService.generateInitialItemResult(parsedQuestion, assessmentRunId);

            store.dispatch(
                responseActions.setItemResult(question, question.contentRevisionId, itemResult),
            );
        }

        return {
            parsedQuestion,
            itemResult,
        };
    },

    assessmentSketchCodesLoaded(sketchCodes) {
        return (dispatch) => {
            dispatch({ type: this.types.ASSESSMENT_SKETCH_CODES_LOADED, sketchCodes });
        };
    },

    assessmentSketchCodeLoaded(sketchCode) {
        return (dispatch) => {
            dispatch({ type: this.types.ASSESSMENT_SKETCH_CODE_LOADED, sketchCode });
        };
    },

    assessmentFeedbackForQuestionsLoaded(feedbacks) {
        return (dispatch) => {
            dispatch({ type: this.types.ASSESSMENT_FEEDBACK_LOADED, feedbacks });
        };
    },

    assessmentResetFeedbacksForQuestion() {
        return (dispatch) => {
            dispatch({ type: this.types.ASSESSMENT_FEEDBACK_RESET });
        };
    },

    setAllQuestionsAsNotParsed() {
        return { type: this.types.ASSESSMENT_QUESTIONS_RESET_PARSED };
    },

    updateSection(section) {
        return { type: this.types.ASSESSMENT_UPDATE_SECTION, section };
    },
};

export default assessmentActions;
